import { useState } from 'react';
import { type IntegrationDefinition } from '@/types/integrations';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, Loader2, ExternalLink } from 'lucide-react';

interface OAuthConnectionDialogProps {
  integration: IntegrationDefinition | null;
  open: boolean;
  onClose: () => void;
  onConnect: (integrationId: string, account: string) => Promise<void>;
}

export function OAuthConnectionDialog({
  integration,
  open,
  onClose,
  onConnect,
}: OAuthConnectionDialogProps) {
  const [authorizing, setAuthorizing] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState('');

  const handleClose = () => {
    setAuthorizing(false);
    setAuthorized(false);
    setConnectedAccount('');
    onClose();
  };

  const handleAuthorize = async () => {
    if (!integration) return;

    setAuthorizing(true);

    // Simulate OAuth popup flow
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Mock successful authorization
    const mockEmail = `user@${integration.name.toLowerCase()}.com`;
    setConnectedAccount(mockEmail);
    setAuthorized(true);
    setAuthorizing(false);
  };

  const handleConnect = async () => {
    if (!integration) return;

    try {
      await onConnect(integration.id, connectedAccount);
      handleClose();
    } catch (error) {
      console.error('Connection failed:', error);
    }
  };

  if (!integration) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-3xl">{integration.logo}</div>
            <DialogTitle>Connect {integration.name}</DialogTitle>
          </div>
          <DialogDescription>{integration.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!authorized ? (
            <>
              <Alert>
                <AlertDescription>
                  You will be redirected to {integration.name} to authorize access. We will need
                  permission to:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Read your account information</li>
                    <li>Send emails on your behalf</li>
                    <li>Access contact information</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <Button
                className="w-full"
                onClick={handleAuthorize}
                disabled={authorizing}
              >
                {authorizing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Authorizing...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Authorize with {integration.name}
                  </>
                )}
              </Button>
            </>
          ) : (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">
                <div className="font-medium mb-1">Authorization successful!</div>
                <div className="text-sm">Connected account: {connectedAccount}</div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {authorized && (
            <Button onClick={handleConnect}>
              Complete Connection
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
